import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.addEventListener('slideshow:select', this.#handleCarouselSelection);
    this.addEventListener('click', this.#handlePaginationClick);
    this.#setupBestSellerBehavior();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.removeEventListener('slideshow:select', this.#handleCarouselSelection);
    this.removeEventListener('click', this.#handlePaginationClick);
  }

  #handleCarouselSelection = (event) => {
    if (!event.target.closest('.resource-list__carousel')) return;

    const page = Math.floor(event.detail.index / 3);
    this.#setActivePaginationPage(page);
  };

  #handlePaginationClick = (event) => {
    const dot = event.target.closest('[data-velora-carousel-page]');
    if (!dot || !this.contains(dot)) return;

    const page = Number(dot.dataset.veloraCarouselPage);
    const carousel = this.querySelector('[data-velora-recs-carousel]');
    if (!carousel || Number.isNaN(page)) return;

    const card = carousel.querySelector('[data-velora-recs-product]');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(carousel).gap) || 28;
    carousel.scrollTo({ left: page * 3 * (card.offsetWidth + gap), behavior: 'smooth' });
    this.#setActivePaginationPage(page);
  };

  #setActivePaginationPage(page) {
    const dots = this.querySelectorAll('[data-velora-carousel-page]');
    if (!dots.length) return;

    const activePage = Math.min(page, dots.length - 1);
    for (const [index, dot] of dots.entries()) {
      const isActive = index === activePage;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    }
  }

  #setupBestSellerBehavior() {
    const carousel = this.querySelector('[data-velora-recs-carousel]');
    if (!carousel || carousel.dataset.veloraReady === 'true') return;
    carousel.dataset.veloraReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let targetScroll = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    let moved = false;
    let animation = 0;

    const follow = () => {
      targetScroll = Math.max(0, Math.min(targetScroll, carousel.scrollWidth - carousel.clientWidth));
      const distance = targetScroll - carousel.scrollLeft;
      carousel.scrollLeft += distance * 0.22;
      if (pointerId !== null || Math.abs(distance) > 0.1) animation = requestAnimationFrame(follow);
    };

    carousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimationFrame(animation);
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      startScroll = targetScroll = carousel.scrollLeft;
      lastTime = performance.now();
      velocity = 0;
      moved = false;
      animation = requestAnimationFrame(follow);
    });

    carousel.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || event.pointerId !== pointerId) return;
      const now = performance.now();
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 5) {
        moved = true;
        carousel.classList.add('is-dragging');
        carousel.setPointerCapture?.(pointerId);
      }
      targetScroll = startScroll - delta;
      const elapsed = Math.max(1, now - lastTime);
      const instantVelocity = (event.clientX - lastX) / elapsed;
      velocity = velocity * 0.82 + instantVelocity * 0.18;
      lastX = event.clientX;
      lastTime = now;
      if (moved) event.preventDefault();
    });

    const release = (event) => {
      if (event.pointerType !== 'mouse' || event.pointerId !== pointerId) return;
      pointerId = null;
      carousel.classList.remove('is-dragging');
      targetScroll -= velocity * 240;
      cancelAnimationFrame(animation);
      animation = requestAnimationFrame(follow);
      setTimeout(() => { moved = false; }, 0);
    };

    carousel.addEventListener('pointerup', release);
    carousel.addEventListener('pointercancel', release);
    carousel.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);

    let scrollFrame = 0;
    carousel.addEventListener('scroll', () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const card = carousel.querySelector('[data-velora-recs-product]');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(carousel).gap) || 28;
        const pageWidth = 3 * (card.offsetWidth + gap);
        this.#setActivePaginationPage(Math.round(carousel.scrollLeft / pageWidth));
      });
    }, { passive: true });

    this.querySelectorAll('[data-velora-recs-product]').forEach((card) => {
      const productUrl = card.dataset.productUrl;
      card.addEventListener('click', (event) => {
        if (!productUrl || event.target.closest('a')) return;
        window.location.href = productUrl;
      });
      card.addEventListener('keydown', (event) => {
        if (!productUrl || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        window.location.href = productUrl;
      });

      const images = card.querySelectorAll('.velora-recs-product__image');
      if (images.length < 2) return;
      const media = card.querySelector('.velora-recs-product__media');
      media.addEventListener('mousemove', (event) => {
        const rect = media.getBoundingClientRect();
        const index = Math.min(images.length - 1, Math.floor(((event.clientX - rect.left) / rect.width) * images.length));
        images.forEach((image, imageIndex) => image.classList.toggle('is-active', imageIndex === index));
      });
      media.addEventListener('mouseleave', () => {
        images.forEach((image, index) => image.classList.toggle('is-active', index === 0));
      });
    });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          setTimeout(() => this.#setupBestSellerBehavior(), 0);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
